import { findNamedItem, ITEM_RARITY_COLORS, MAX_ITEM_UPGRADE_LEVEL, type ItemEffectKind, type ItemRarity, type WieldedItemTier } from '@sorrem/shared';
import type { ItemEffectLook, ItemLooks } from '@sorrem/engine';

/** The effects each rarity above Hollow shows (#287, #295), each rarity adding to the last; Hollow items stay plain. */
const RESONANT_EFFECTS: readonly ItemEffectKind[] = ['glow', 'motes', 'edge-sheen'];
const RARITY_EFFECTS: Readonly<Partial<Record<ItemRarity, readonly ItemEffectKind[]>>> = {
    uncommon: ['glow'],
    rare: ['glow', 'motes'],
    epic: RESONANT_EFFECTS,
    // Named items (Unforgotten, Empyreal) trace their swings too, unless their own look says otherwise.
    legendary: [...RESONANT_EFFECTS, 'swing-trail'],
    mythic: [...RESONANT_EFFECTS, 'swing-trail'],
};
/** The share of full strength an item has before any upgrade. */
const BASE_STRENGTH = 0.35;

/**
 * How an item's rarity and upgrade level show on its model: the rarity sets
 * the color and which effects it shows, the upgrade level how strong they are.
 * A named item's own look (`NamedItemDefinition.look`, from the page's named
 * item catalog) replaces each part it sets. Undefined for a Hollow item, or one
 * with no rolled copy.
 */
export function findItemEffectLook(
    instance: { readonly rarity: ItemRarity; readonly upgradeLevel: number; readonly namedItemId?: string } | undefined,
): ItemEffectLook | undefined {
    const effects = instance === undefined ? undefined : RARITY_EFFECTS[instance.rarity];
    if (instance === undefined || effects === undefined) {
        return undefined;
    }
    const upgradeShare = Math.min(1, Math.max(0, instance.upgradeLevel / MAX_ITEM_UPGRADE_LEVEL));
    const own = findNamedItem(instance.namedItemId)?.look;
    return {
        colorHex: own?.colorHex ?? ITEM_RARITY_COLORS[instance.rarity],
        effects: own?.effects ?? effects,
        strength: own?.strength ?? BASE_STRENGTH + (1 - BASE_STRENGTH) * upgradeShare,
    };
}

/** The looks of a character's wielded items (`resolveWieldedItemTiers`), keyed by model id; Hollow items are left out. */
export function resolveItemLooks(tiers: Readonly<Record<string, WieldedItemTier>> | undefined): ItemLooks {
    const looks: Record<string, ItemEffectLook> = {};
    for (const [modelId, tier] of Object.entries(tiers ?? {})) {
        const itemLook = findItemEffectLook(tier);
        if (itemLook !== undefined) looks[modelId] = itemLook;
    }
    return looks;
}
